import { computed, ComputedRef, inject, InjectionKey } from 'vue';
import { navigationService } from 'vue-spatialnavigation';
import { focusElementById } from '@/helpers/focus';

//Subset of rail props we need
export interface RailNavigationProps {
  railIndex: number;
  railDidNavigateDown?: (railIndex: number) => void;
}

export const RailTopFocusId: InjectionKey<() => string> = Symbol('railTopFocusId');

export const RailBottomFocusId: InjectionKey<() => string | undefined> =
  Symbol('railBottomFocusId');

export interface RailNavigation {
  containerId: ComputedRef<string>;
  itemId: (index: number) => string;
  handleUp: () => void;
  handleDown: () => void;
  handleLeftFallback: () => void;
}

export function useRailNavigation(
  props: RailNavigationProps,
  topFocusId?: string
): RailNavigation {
  // `inject` has to run unconditionally during setup, so the explicit argument
  // is applied where the id is read rather than by skipping the injection.
  const injectedTopFocusId = inject(RailTopFocusId, () => 'nav-home');
  const resolveTopFocusId = () => topFocusId ?? injectedTopFocusId();
  const injectedBottomFocusId = inject(RailBottomFocusId, () => undefined);

  const containerId = computed(() => `rail-container-${props.railIndex}`);

  const itemId = (index: number) => `rail-${props.railIndex}-${index}`;

  /**
   * Resets this rail's horizontal scroll before focus leaves it, so coming back
   * to the rail later starts from its first item rather than mid-row.
   */
  const resetScroll = () => {
    const container = document.getElementById(containerId.value);
    container?.scrollTo({ left: 0, behavior: 'smooth' });
  };

  /**
   * Hands focus to `targetId` if the navigation engine knows about it. Returns
   * whether the move happened, so callers can skip their side effects when
   * there is nothing to move to (e.g. Down from the last mounted rail).
   */
  const moveFocusTo = (targetId: string): boolean => {
    if (!navigationService.getFocusElementById(targetId)) return false;

    resetScroll();

    return focusElementById(targetId, { scroll: true });
  };

  const handleUp = () => {
    const index = props.railIndex;
    moveFocusTo(index > 0 ? `rail-${index - 1}-0` : resolveTopFocusId());
  };

  const handleDown = () => {
    const nextRailIndex = props.railIndex + 1;
    if (moveFocusTo(`rail-${nextRailIndex}-0`)) {
      props.railDidNavigateDown?.(nextRailIndex);
      return;
    }

    
    const bottomFocusId = injectedBottomFocusId();
    if (bottomFocusId) moveFocusTo(bottomFocusId);
  };

  const handleLeftFallback = () => {
    moveFocusTo(resolveTopFocusId());
  };

  return { containerId, itemId, handleUp, handleDown, handleLeftFallback };
}
